import type {
    MiniAppEdgeToEdge,
    MiniAppInsets,
    MiniAppOs,
    MiniAppTheme,
    MiniAppViewport,
    Platform,
} from './types';


/**
 * The host names the frame `exode-miniapp:<json>` (the `name` of the iframe, `window.name` inside):
 * the scheme, the colors and the insets are known to the app before its first frame, before the
 * handshake. The address of the app stays as it is — older apps never see it
 */
export const BOOT_NAME_PREFIX = 'exode-miniapp:';

/** What the host tells the app before the handshake */
export interface MiniAppBoot {
    theme?: MiniAppTheme;
    os?: MiniAppOs;
    platform?: Platform;
    viewport?: MiniAppViewport;
    language?: string;
}

export interface MiniAppEnvironment {
    theme?: MiniAppTheme | null;
    os?: MiniAppOs | null;
    platform?: Platform | null;
    viewport?: MiniAppViewport | null;
}

export interface ApplyEnvironmentOptions {
    /** `true` (or `'auto'`) pads `<body>` by the insets, `'manual'` leaves it to the app */
    edgeToEdge?: MiniAppEdgeToEdge | 'auto';
    /** The scheme of the host on the page: `color-scheme`, `.dark`, the background and the text of `<body>` */
    applyTheme?: boolean;
}

const zeroInsets: MiniAppInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * The boot of the host from the name of the frame
 * @param {string} name — `window.name`
 * @returns {MiniAppBoot | null}
 */
export function parseBoot(name: string | null | undefined): MiniAppBoot | null {
    if (!name || name.indexOf(BOOT_NAME_PREFIX) !== 0) {
        return null;
    }

    try {
        const boot = JSON.parse(name.slice(BOOT_NAME_PREFIX.length));

        return boot && typeof boot === 'object' ? boot as MiniAppBoot : null;
    } catch {
        return null;
    }
}

/** The boot of the host of this frame; null on the server and outside the host */
export function readBoot(): MiniAppBoot | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return parseBoot(window.name);
    } catch {
        return null;
    }
}

/**
 * What the app keeps free at each edge: the chrome of the host or the safe area, the larger one
 * @param {MiniAppViewport | null} viewport
 * @returns {MiniAppInsets}
 */
export function resolveInsets(viewport: MiniAppViewport | null | undefined): MiniAppInsets {
    if (!viewport) {
        return { ...zeroInsets };
    }

    const { safeArea = zeroInsets, content = zeroInsets } = viewport;

    return {
        top: Math.max(safeArea.top || 0, content.top || 0),
        right: Math.max(safeArea.right || 0, content.right || 0),
        bottom: Math.max(safeArea.bottom || 0, content.bottom || 0),
        left: Math.max(safeArea.left || 0, content.left || 0),
    };
}

/**
 * Puts the environment of the host on `<html>`. The boot script carries its source
 * (`exodeBootScript`), so it is self-contained: no reference outside of it and no function bound
 * to a name inside (a bundler with `keepNames` would wrap such a function in its own helper).
 * `env` is the whole environment: what it lacks is taken off the page
 * @param {HTMLElement} root
 * @param {MiniAppEnvironment} env
 * @param {ApplyEnvironmentOptions} options
 */
function applyEnvironmentTo(
    root: HTMLElement,
    env: MiniAppEnvironment,
    options: ApplyEnvironmentOptions,
): void {
    const doc = root.ownerDocument;
    const style = root.style;
    const theme = env.theme;
    const viewport = env.viewport;
    const edge = options.edgeToEdge === true ? 'auto' : options.edgeToEdge;

    // The theme of a host 0.4+ (its accent, its colors): an older one tells the scheme alone
    const isHostTheme = !!(theme && (theme.accent || theme.colors));

    const colorKeys = [
        'background', 'content', 'elevated', 'text', 'textSecondary', 'textTertiary',
        'accent', 'accentForeground', 'separator', 'destructive', 'positive',
    ];

    const attributes: [ string, string | null | undefined ][] = [
        [ 'data-exode-os', env.os ],
        [ 'data-exode-platform', env.platform ],
        [ 'data-exode-scheme', theme && theme.scheme ],
        [ 'data-exode-edge', viewport && edge ? edge : null ],
        [ 'data-exode-theme', options.applyTheme && isHostTheme ? '1' : null ],
        [ 'data-exode-fullscreen', viewport && viewport.isFullscreen ? '1' : null ],
        [ 'data-exode-orientation', viewport && viewport.orientation ],
    ];

    // The base style goes first in <head>, so the styles of the app win over it — but the padding of
    // an edge to edge body: a reset `body { padding: 0 }` would put the content under the chrome
    // (an app that pads itself takes edgeToEdge: 'manual')
    if (doc && !doc.getElementById('exode-miniapp-base')) {
        const base = doc.createElement('style');
        const parent = doc.head || root;

        base.id = 'exode-miniapp-base';
        base.textContent = ''
            + ':where(html){-webkit-text-size-adjust:100%;text-size-adjust:100%;}'
            + ':where(html[data-exode-platform="native"]){-webkit-tap-highlight-color:transparent;}'
            // iOS: the document is 1px taller than the frame at least, so a screen shorter than it springs
            // as a page does — a frame that does not scroll gives the gesture to the page of the host
            + ':where(html[data-exode-os="ios"]){min-height:calc(100% + 1px);}'
            + ':where(html[data-exode-edge]){scroll-padding-top:var(--exode-inset-top,0px);'
            + 'scroll-padding-bottom:var(--exode-inset-bottom,0px);}'
            + 'html[data-exode-edge="auto"] body{box-sizing:border-box;'
            + 'padding-top:var(--exode-inset-top,0px);padding-right:var(--exode-inset-right,0px);'
            + 'padding-bottom:var(--exode-inset-bottom,0px);padding-left:var(--exode-inset-left,0px);}'
            + ':where(html[data-exode-theme]){color-scheme:var(--exode-scheme,normal);}'
            + ':where(html[data-exode-theme]) :where(body){background-color:var(--exode-color-background,transparent);'
            + 'color:var(--exode-color-text,inherit);}';

        parent.insertBefore(base, parent.firstChild);
    }

    for (const [ name, value ] of attributes) {
        value ? root.setAttribute(name, value) : root.removeAttribute(name);
    }

    if (theme && theme.scheme) {
        style.setProperty('--exode-scheme', theme.scheme);
    }

    if (options.applyTheme && isHostTheme) {
        root.classList.toggle('dark', theme!.scheme === 'dark');
    }

    if (theme) {
        // A new theme replaces the old one whole: its colors that are gone go off the page too
        for (let i = style.length - 1; i >= 0; i--) {
            if (style[i].indexOf('--exode-color-') === 0) {
                style.removeProperty(style[i]);
            }
        }

        theme.accent
            ? style.setProperty('--exode-accent', String(theme.accent))
            : style.removeProperty('--exode-accent');

        const colors = (theme.colors || {}) as Record<string, unknown>;

        for (const key of colorKeys) {
            const value = colors[key];

            if (typeof value === 'string' && value.length <= 200) {
                style.setProperty('--exode-color-' + key.replace(/[A-Z]/g, (letter) => '-' + letter.toLowerCase()), value);
            }
        }
    }

    if (viewport) {
        const zero = { top: 0, right: 0, bottom: 0, left: 0 };
        const safeArea = viewport.safeArea || zero;
        const content = viewport.content || zero;
        const inset = { ...zero };

        for (const side of [ 'top', 'right', 'bottom', 'left' ] as const) {
            inset[side] = Math.max(safeArea[side] || 0, content[side] || 0);

            style.setProperty('--exode-safe-area-inset-' + side, Math.max(0, Math.round(safeArea[side] || 0)) + 'px');
            style.setProperty('--exode-content-inset-' + side, Math.max(0, Math.round(content[side] || 0)) + 'px');
            style.setProperty('--exode-inset-' + side, Math.max(0, Math.round(inset[side])) + 'px');
        }

        style.setProperty('--exode-viewport-width', Math.max(0, Math.round(viewport.width || 0)) + 'px');
        style.setProperty('--exode-viewport-height', Math.max(0, Math.round(viewport.height || 0)) + 'px');
        style.setProperty('--exode-content-height', Math.max(0, Math.round((viewport.height || 0) - inset.top - inset.bottom)) + 'px');
    }
}

/**
 * Puts the environment of the host on `<html>` of this document (no-op on the server)
 * @param {MiniAppEnvironment} env
 * @param {ApplyEnvironmentOptions} options
 */
export function applyEnvironment(
    env: MiniAppEnvironment,
    options: ApplyEnvironmentOptions = {},
): void {
    if (typeof document === 'undefined' || !document.documentElement) {
        return;
    }

    applyEnvironmentTo(document.documentElement, env, options);
}

/**
 * An inline script for `<head>` of a server-rendered app: the scheme, the colors and the insets
 * of the host are on the page before its first paint — no flash of the default theme, no jump
 * of the content under the header. Pass the same options as to `ExodeMiniAppProvider`, and
 * `suppressHydrationWarning` on `<html>`: the script changes its attributes before React
 * @param {ApplyEnvironmentOptions} options
 * @returns {string}
 */
export function exodeBootScript(options: ApplyEnvironmentOptions = { edgeToEdge: true, applyTheme: true }): string {
    return '(function(){try{'
        + 'var n=window.name||"",p=' + JSON.stringify(BOOT_NAME_PREFIX) + ';'
        + 'if(n.indexOf(p)!==0){return;}'
        + 'var b=JSON.parse(n.slice(p.length));'
        + '(' + applyEnvironmentTo.toString() + ')(document.documentElement,b,' + JSON.stringify(options) + ');'
        + '}catch(e){}})();';
}
